import React, { useState, useEffect } from 'react';
import {
  GraduationCap,
  Clock,
  DollarSign,
  FileText,
  CheckCircle,
  Mail,
  Target,
  Globe,
  Loader2,
  Building2
} from 'lucide-react';

const EstanciasAcademicas = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [estanciasRes, generalesRes] = await Promise.all([
          fetch(`/data/estancias.json?v=${Date.now()}`),
          fetch(`/data/generales.json?v=${Date.now()}`)
        ]);

        if (!estanciasRes.ok) {
          throw new Error('Error al cargar los datos de estancias academicas');
        }
        if (!generalesRes.ok) {
          throw new Error('Error al cargar los datos generales');
        }

        const [jsonData, generalesData] = await Promise.all([
          estanciasRes.json(),
          generalesRes.json()
        ]);

        setData(jsonData);
        setDatosBancarios(generalesData.datosBancarios);
        setError(null);
      } catch (err) {
        console.error('Error cargando datos de estancias:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando informacion de estancias academicas...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 text-lg mb-4">Error: {error || 'No se pudieron cargar los datos'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#0b742f] text-white px-6 py-2 rounded-lg hover:bg-[#085a24]"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
            <Globe className="w-5 h-5" />
            <span className="text-sm font-semibold">Programa de Vinculacion Academica</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{data.titulo}</h1>
          <p className="text-xl text-green-100 max-w-4xl">{data.resumen}</p>
          {data.definicion && (
            <p className="text-md text-green-100 max-w-4xl mt-4">{data.definicion}</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6 -mt-12 mb-16 relative z-10">
          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#0b742f]">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-[#0b742f]" />
              </div>
              <h3 className="font-bold text-gray-900">Duracion</h3>
            </div>
            <p className="text-2xl font-bold text-[#0b742f]">{data.duracion}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-[#e87729]">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[#e87729]/10 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-[#e87729]" />
              </div>
              <h3 className="font-bold text-gray-900">Modalidad</h3>
            </div>
            <p className="text-2xl font-bold text-[#e87729]">{data.modalidad}</p>
          </div>

          <div className="bg-white rounded-xl shadow-xl p-6 border-t-4 border-green-600">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-600/10 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-gray-900">Inversion Inicial</h3>
            </div>
            <p className="text-2xl font-bold text-green-600">{data.inversion?.conMembresia}</p>
          </div>
        </div>

        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <h2 className="text-3xl font-bold mb-4">Objetivos Clave de la Estancia</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {(data.objetivosClave || []).map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Objetivo General</h2>
            </div>
            <p className="text-lg leading-relaxed text-orange-50">{data.objetivoGeneral}</p>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#0b742f] p-3 rounded-lg">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">{data.estrategiaSniiTitulo}</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">{data.estrategiaSniiIntroduccion}</p>
            <div className="space-y-4">
              {(data.estrategiaSnii || []).map((item, index) => (
                <div key={index} className="bg-green-50 border-l-4 border-[#0b742f] rounded-r-xl p-5">
                  <h3 className="font-bold text-gray-900 mb-2">{item.titulo}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100">
            <h2 className="text-3xl font-bold mb-4">Etapas del Proceso de Redacción en Estancia</h2>
            <div className="space-y-4">
              {(data.etapas || []).map((etapa, i) => (
                <div key={i} className="p-4 bg-green-50 rounded-lg border-l-4 border-[#0b742f]">
                  <h3 className="font-bold text-gray-900 mb-1">{etapa.nombre}</h3>
                  <p className="text-gray-700">{etapa.descripcion}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="w-8 h-8" />
                <h3 className="text-2xl font-bold">Inversion por Participante</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="bg-white/20 rounded-xl p-4">
                  <p className="text-green-100 text-sm mb-1">Con membresia</p>
                  <p className="text-3xl font-bold">{data.inversion?.conMembresia}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-green-100 text-sm mb-1">Sin membresia</p>
                  <p className="text-3xl font-bold">{data.inversion?.sinMembresia}</p>
                </div>
                <p className="text-green-100 text-sm">*{data.inversion?.nota}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-8 h-8 text-[#0b742f]" />
                <h3 className="text-2xl font-bold text-gray-900">El Costo Incluye</h3>
              </div>
              <ul className="space-y-3">
                {(data.incluye || []).map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 bg-orange-50 border-l-4 border-[#e87729] rounded-r-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Nota importante:</strong> {data.notaImportante}
                </p>
              </div>
            </div>
          </div>
        </section>

        {datosBancarios && (
          <section className="mb-16">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl shadow-xl p-8 md:p-12 border-t-4 border-gray-700">
              <h3 className="font-bold text-gray-900 mb-6 text-2xl flex items-center gap-3">
                <Building2 className="w-7 h-7 text-gray-700" />
                Datos Bancarios para Pagos
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Beneficiario</p>
                  <p className="font-bold text-gray-900">{datosBancarios.titular}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Banco</p>
                  <p className="font-bold text-gray-900">{datosBancarios.banco}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">Cuenta</p>
                  <p className="font-bold text-gray-900 text-lg">{datosBancarios.numeroCuenta}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-600 mb-1">CLABE Interbancaria</p>
                  <p className="font-bold text-gray-900 text-lg">{datosBancarios.clabe}</p>
                </div>
                {datosBancarios.swift && (
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">SWIFT</p>
                    <p className="font-bold text-gray-900">{datosBancarios.swift}</p>
                  </div>
                )}
                {datosBancarios.sucursal && (
                  <div className="bg-white rounded-lg p-4 shadow-sm md:col-span-2">
                    <p className="text-sm text-gray-600 mb-1">Sucursal</p>
                    <p className="font-bold text-gray-900 text-sm">{datosBancarios.sucursal}</p>
                  </div>
                )}
              </div>
              <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Importante:</strong> Todos los pagos deberan ser reportados al correo:{' '}
                  <a href={`mailto:${data.contacto}`} className="text-green-600 font-bold hover:underline break-all">
                    {data.contacto}
                  </a>
                </p>
              </div>
            </div>
          </section>
        )}

        <section>
          <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-8 md:p-12 text-center relative">
              <div className="relative z-10">
                <div className="bg-white/10 backdrop-blur-sm rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Para mas informes e inscripciones</h2>
                <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                  Contactanos para iniciar tu proceso de estancia academica corta.
                </p>
                <a
                  href={`mailto:${data.contacto}`}
                  className="inline-flex items-center gap-3 bg-white text-[#0b742f] px-10 py-5 rounded-xl font-bold text-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl"
                >
                  <Mail className="w-7 h-7 flex-shrink-0" />
                  <span className="break-all">{data.contacto}</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EstanciasAcademicas;
